import React from 'react'
import qs from 'qs'
import { makeStyles } from '@mui/styles'
import { useSelector } from 'react-redux'
import clsx from 'clsx'

import ButtonIconSquare, { VARIANTS as ButtonIconSquareVariants } from '@/modules/base/components/buttons/icon-square/button-icon-square'
import SubscriptionItem from '@/modules/base/components/modal/upgrade-modal/subscription-selection/subscription-item/subscription-item'
import subscriptionPlanOrder from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/mboSubscriptionPlanOrder'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { ProductTypes } from '@/modules/base/util/constants'
import { withoutDemoSubscription } from '@/utils/shop-subscriptions'
import lang from '@/lang'

import ComingSoonSvg from '@assets/images/icons/coming-soon.svg'
import LogoMain from '@assets/images/product/main_blue_with_icon.svg'
import LogoDocument from '@assets/images/product/document_blue_with_icon.svg'
import LogoStaff from '@assets/images/product/staff_blue_with_icon.svg'

const SubscriptionLogos = {
  [ProductTypes.MAIN]: LogoMain,
  [ProductTypes.DOCUMENT]: LogoDocument,
  [ProductTypes.STAFF]: LogoStaff,
}

export const getUpgradeLink = (product, relatedSubscription, plan) => {
  if (!product.upgradeLinks?.length || !relatedSubscription) return null
  if (product.type === ProductTypes.MAIN) {
    if (!product.serial) return product.upgradeLinks?.[0]?.link
    const relatedKey = subscriptionPlanOrder[product.type]?.find(order => order.name === relatedSubscription.type)?.key
    const upgradeLinkObj = relatedKey === plan.name ? product.upgradeLinks.find(p => p.name === 'module') : product.upgradeLinks.find(p => p.name === plan.name)
    const queryParamsObj = upgradeLinkObj?.params?.reduce((acc, param) => {
      if (param.type === 'mapping') {
        acc[param.key] = param.options[product[param.valueGetter]]
      } else if (param.type === 'value') {
        acc[param.key] = product[param.valueGetter]
      } else if (param.type === 'staticValue') {
        acc[param.key] = param.value
      }
      return acc
    }, {})
    const queryParams = qs.stringify(queryParamsObj)
    const upgradeLink = `${upgradeLinkObj?.link}?${queryParams}`
    return upgradeLink
  }
  if (relatedSubscription.baseType) {
    const baseTypeKey = subscriptionPlanOrder[product.type]?.find(order => order.name === relatedSubscription.baseType).key
    const typeKey = subscriptionPlanOrder[product.type]?.find(order => order.name === relatedSubscription.type).key
    if (baseTypeKey && typeKey) {
      return product.upgradeLinks.find(ul => ul.name === baseTypeKey && ul.upgradeFrom === typeKey)?.link
    }
  }
  const upgradeLink = product.upgradeLinks.find(p => p.name === subscriptionPlanOrder[product.type]?.find(order => order.name === relatedSubscription.type).key)?.link
  return upgradeLink
}

const useStyles = makeStyles((theme) => ({
  dialog__content: {
    padding: 50,
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
    width: '100%',
    margin: 0,
    minHeight: '100%',
    height: 'auto',
    backgroundColor: theme.white,
  },
  closeButton: {
    width: 175,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  subscriptionItemWrapper: ({ comingSoon }) => ({
    height: comingSoon ? '100%' : 'unset',
    alignItems: comingSoon ? 'center' : 'unset',
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
    columnGap: 20,
    justifyContent: 'center',

    [theme.devices.mobile]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
  subTitle: {
    fontSize: 22,
    fontWeight: theme.fontWeights.medium,
    margin: '12px 0',
    color: theme.planUpgradeModal.colors.blue,
  },
  legalMessages: {
    fontSize: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0px 20px',
  },
  comingSoonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 30,
    fontWeight: 500,
  },
}))

const SubscriptionSelection = (props) => {
  const { onClose, hasPrevious, onPrevious, product, hideButton, className } = props

  const comingSoon = !product?.plans?.length
  const classes = useStyles({ comingSoon })

  const { subscriptions } = useSelector(state => state.subscription.details)
  const relatedSubscription = withoutDemoSubscription(subscriptions).find(s => s.typeOfSubscription === product.type && !s.isTrial)
  const legalMessagesEle = product?.legalMessages?.map(message => (
    <div className={classes.legalMessages} dangerouslySetInnerHTML={{ __html: message.html }}></div>
  ))

  let currentlyNotAvailable = null
  if (comingSoon) {
    currentlyNotAvailable = (
      <div className={classes.comingSoonWrapper}>
        <SVGIcon svg={ComingSoonSvg} customClass={classes.icon} height='175px' width='175px' />
        <div>Steht in Kürze zur Verfügung.</div>
      </div>
    )
  }

  const plansHaveReducedPrice = product?.plans?.some(p => p.reducedPrice)
  return (
    <div className={clsx(classes.dialog__content, className)}>
      <div className={classes.header}>
        {SubscriptionLogos[product.type] && <SVGIcon svg={SubscriptionLogos[product.type]} height={50} width={350} />}
        <p className={classes.subTitle}>{lang.upgradeModal.choosePlan}</p>
      </div>
      <div className={classes.subscriptionItemWrapper}>
        {currentlyNotAvailable}
        {product?.plans?.map((plan, index) =>
          (<SubscriptionItem
            plan={plan}
            upgradeLink={getUpgradeLink(product, relatedSubscription, plan)}
            typeOfSubscription={product.type}
            disabled={
              relatedSubscription
              && (subscriptionPlanOrder[product.type]?.findLastIndex(order => order.name === relatedSubscription.type)
              > subscriptionPlanOrder[product.type]?.findLastIndex(order => order.key === plan.name) && index !== product?.plans.length - 1)
            }
            itemCount={product?.plans?.length}
            relatedSubscription={relatedSubscription}
            plansHaveReducedPrice={plansHaveReducedPrice}
            serial={product.serial}
          />))}
      </div>
      {legalMessagesEle}
      {!hideButton
      && <div className={classes.footer}>
        <ButtonIconSquare
          label={hasPrevious ? lang.backButtonLabel : lang.closeButtonLabel}
          onClick={hasPrevious ? onPrevious : onClose}
          customClass={classes.closeButton}
          variant={ButtonIconSquareVariants.outline}
        />
      </div>}
    </div>
  )
}

export default SubscriptionSelection
